import jobServices from 'ApiService/Student/jobServices';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { MdOutlineCloudUpload, MdDownload, MdDeleteOutline } from 'react-icons/md';
import { AiOutlineDelete } from 'react-icons/ai';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const allowedExtensions = ['pdf', 'doc', 'docx'];
const RESUME_TYPES = ['Fresher', 'Experience'];
const TYPE_LABELS = { Fresher: 'fresher', Experience: 'experience', Legacy: '' };

const Index = () => {
  const [typedResumes, setTypedResumes] = useState({ Fresher: null, Experience: null });
  const [legacyResume, setLegacyResume] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, type: null });
  const [uploadConfirmation, setUploadConfirmation] = useState({ isOpen: false });

  const fetchResume = async () => {
    setLoading(true);
    try {
      const response = await jobServices.getResume();
      if (response.data.success) {
        const data = response.data.data || {};
        setTypedResumes({
          Fresher: data.fresher || null,
          Experience: data.experience || null,
        });
        setLegacyResume(data.legacyResume || null);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch resume.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResume();
  }, []);

  const handleFileChange = (files) => {
    if (!selectedType) {
      toast.error('Please select Fresher or Experience before choosing a file.');
      return;
    }
    if (files.length === 0) return;
    const file = files[0];
    const extension = file.name.split('.').pop().toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      toast.error(`Invalid file format. Allowed: ${allowedExtensions.join(', ')}`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10 MB limit');
      return;
    }

    setNewFile(file);
    toast.success(`File selected for ${selectedType} resume!`);
  };

  const confirmUpload = () => {
    if (!selectedType) {
      toast.error('Please select Fresher or Experience.');
      return;
    }
    if (!newFile) {
      toast.error('No new file selected for upload.');
      return;
    }
    setUploadConfirmation({ isOpen: true });
  };

  const handleSubmit = async () => {
    try {
      setUploadConfirmation({ isOpen: false });

      if (!selectedType || !newFile) {
        toast.error('Please select resume type and file.');
        return;
      }

      setUploading(true);

      const formData = new FormData();
      formData.append('resume', newFile);
      formData.append('resumeType', selectedType);

      const response = await jobServices.uploadResume(formData);
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Failed to upload resume');
      }

      toast.success(`${selectedType} resume uploaded successfully`);
      setNewFile(null);
      await fetchResume();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to upload resume');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const { type } = deleteConfirmation;
      setDeleteConfirmation({ isOpen: false, type: null });
      const response = await jobServices.deleteResume(type);
      if (response.data.success) {
        if (type === 'Legacy') {
          setLegacyResume(null);
        } else {
          setTypedResumes((prev) => ({ ...prev, [type]: null }));
        }
        toast.success(
          type === 'Legacy' ? 'Resume deleted successfully' : `${type} resume deleted successfully`
        );
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete resume');
    }
  };

  const ResumeCard = ({ title, resume, onDelete }) =>
    resume ? (
      <div className="relative mb-4">
        <div className="flex items-center rounded-lg border p-4">
          <div className="flex h-20 w-20 items-center justify-center bg-gray-100 dark:bg-navy-700">
            <span className="text-sm">{resume.fileType?.toUpperCase()}</span>
          </div>
          <div className="ml-4 flex-grow">
            <p className="font-medium text-navy-700 dark:text-white">{resume.fileName}</p>
            <p className="text-sm text-gray-500">
              {resume.fileSize ? `${(resume.fileSize / 1024 / 1024).toFixed(2)} MB · ` : ''}
              Uploaded on: {new Date(resume.uploadedOn).toLocaleDateString()}
            </p>
          </div>
          <a
            href={`${API_BASE_URL}/${resume.filePath}`}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 flex h-9 items-center gap-1 rounded-lg bg-brand-500 px-3 text-sm text-white hover:bg-brand-600"
          >
            <MdDownload size={16} />
            Download
          </a>
          <button
            onClick={onDelete}
            className="ml-2 flex h-9 w-9 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-700"
            title={`Delete ${title}`}
          >
            <MdDeleteOutline size={18} />
          </button>
        </div>
      </div>
    ) : (
      <p className="mb-4 text-center text-gray-500">No {title.toLowerCase()} uploaded yet</p>
    );

  return (
    <div className="mt-3 flex h-full w-full flex-col gap-[20px] rounded-[20px]">
      <div className="md:2/3 mx-auto h-full w-5/6 md:px-3 3xl:w-7/12">
        <div className="h-full w-full rounded-[20px] px-3 pt-7 md:px-8">
          <h4 className="mb-6 pt-[5px] text-xl font-bold text-navy-700 dark:text-white">
            Upload Resume
          </h4>

          <div className="mb-6">
            <h5 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
              Select Resume Type
            </h5>
            <div className="flex items-center gap-6">
              {RESUME_TYPES.map((type) => (
                <label
                  key={type}
                  className={`flex cursor-pointer items-center gap-2 rounded-xl border px-5 py-2 transition ${
                    selectedType === type
                      ? 'border-brand-500 bg-brand-50'
                      : 'border-gray-200 hover:border-brand-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="resumeType"
                    value={type}
                    checked={selectedType === type}
                    onChange={() => {
                      if (newFile) setNewFile(null);
                      setSelectedType(type);
                    }}
                    className="h-4 w-4 accent-brand-500"
                  />
                  <span className="font-medium text-navy-700 dark:text-white">
                    {type}
                  </span>
                </label>
              ))}
            </div>
            {!selectedType && (
              <p className="mt-2 text-sm text-gray-500">
                Choose whether this is a fresher or experienced resume.
              </p>
            )}
          </div>

          <div className="mb-6">
            <div className="relative flex h-[225px] w-full flex-col items-center justify-center rounded-xl border-[1px] border-dashed border-gray-200 bg-gray-100 dark:border-none dark:bg-navy-700">
              <input
                type="file"
                onChange={(e) => handleFileChange(e.target.files)}
                className="absolute h-full w-full cursor-pointer opacity-0"
                accept={allowedExtensions.map((ext) => `.${ext}`).join(',')}
                disabled={!selectedType}
              />
              <div className="flex items-center justify-center">
                <MdOutlineCloudUpload className="text-[80px] text-brand-500 dark:text-white" />
              </div>
              <p className="text-lg font-bold text-navy-700 dark:text-white">
                Drop your {selectedType ? `${selectedType.toLowerCase()} ` : ''}resume here, or
                browse
              </p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                PDF / DOC / DOCX, up to 10 MB
              </p>
            </div>
          </div>

          <div className="mt-6">
            <h5 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
              New Resume to Upload
            </h5>
            {newFile ? (
              <div className="relative mb-4">
                <div className="flex items-center rounded-lg border p-4">
                  <div className="flex h-20 w-20 items-center justify-center bg-gray-100 dark:bg-navy-700">
                    <span className="text-sm">
                      {newFile.name.split('.').pop().toUpperCase()}
                    </span>
                  </div>
                  <div className="ml-4 flex-grow">
                    <p className="font-medium text-navy-700 dark:text-white">{newFile.name}</p>
                    <p className="text-sm text-gray-500">
                      {(newFile.size / 1024 / 1024).toFixed(2)} MB · Type:{' '}
                      <span className="font-semibold">{selectedType}</span>
                    </p>
                  </div>
                  <button
                    onClick={() => setNewFile(null)}
                    className="ml-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-700"
                  >
                    <AiOutlineDelete size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500">No new file selected</p>
            )}
            <div className="mt-8 flex justify-center">
              <button
                onClick={confirmUpload}
                disabled={!selectedType || !newFile || uploading}
                className="rounded-xl bg-brand-500 px-8 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : `Upload ${selectedType || ''} Resume`}
              </button>
            </div>
          </div>

          <div className="mt-6">
            <h5 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
              Current Resumes
            </h5>
            {loading ? (
              <p className="text-center text-gray-500">Loading...</p>
            ) : (
              <>
                <h6 className="mb-2 font-semibold text-navy-700 dark:text-white">Fresher</h6>
                <ResumeCard
                  title="Fresher Resume"
                  resume={typedResumes.Fresher}
                  onDelete={() => setDeleteConfirmation({ isOpen: true, type: 'Fresher' })}
                />
                <h6 className="mb-2 mt-6 font-semibold text-navy-700 dark:text-white">
                  Experience
                </h6>
                <ResumeCard
                  title="Experience Resume"
                  resume={typedResumes.Experience}
                  onDelete={() => setDeleteConfirmation({ isOpen: true, type: 'Experience' })}
                />
                {legacyResume && (
                  <>
                    <h6 className="mb-2 mt-6 font-semibold text-navy-700 dark:text-white">
                      Previous Resume
                    </h6>
                    <ResumeCard
                      title="Previous Resume"
                      resume={legacyResume}
                      onDelete={() => setDeleteConfirmation({ isOpen: true, type: 'Legacy' })}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, type: null })}
        onConfirm={handleDelete}
        title="Confirm Deletion"
        message={`Are you sure you want to delete your ${
          TYPE_LABELS[deleteConfirmation.type] ?? ''
        } resume? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        icon="warning"
      />

      <ConfirmationModal
        isOpen={uploadConfirmation.isOpen}
        onClose={() => setUploadConfirmation({ isOpen: false })}
        onConfirm={handleSubmit}
        title="Confirm Upload"
        message={`Are you sure you want to upload this ${selectedType} resume?${
          typedResumes[selectedType] ? ' Your existing ' + selectedType.toLowerCase() + ' resume will be replaced.' : ''
        }`}
        confirmText="Upload"
        cancelText="Cancel"
        icon="info"
      />
    </div>
  );
};

export default Index;
